import { useEffect, useMemo, useState } from "react";

/**
 * Client-side pagination hook. Slices `items` into pages of `pageSize`.
 * Automatically resets to page 1 when the source list changes size.
 * Returns { page, setPage, totalPages, pageItems, rangeStart, rangeEnd }.
 */
export default function usePagination(items, pageSize = 20) {
  const [page, setPage] = useState(1);
  const total = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Snap to a valid page whenever the list size changes.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (items || []).slice(start, start + pageSize);
  }, [items, page, pageSize]);

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total };
}
